﻿import type { ColorSection } from 'components/GeneralGaugeChart/GeneralGauge';
import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';

export interface CategoryReport {
  Category: string;
  CategoryDescription: string;
  CategoryId: number;
  Custom: boolean;
  Reports: Report[];
}

export interface Report {
  BaseReportDescription: string;
  BaseReportName: string;
  Category: null;
  CategoryName: string;
  createDate: string;
  Description: string;
  Filters: null;
  HasResults: boolean;
  Id: number;
  Name: string;
  RelatedReports?: Report[];
  ReportFilters: ReportFilter[];

  // Custom Reports props
  ReportId: number;
  ReportSections: ReportSection[];
  ScopeId: number;
  ScopeName: string | null;
  ScopeObjectId: number;
  StoredProcedure: string;
  TileTemplateId: number;
  URL: string;
}

export interface ReportSection {
  Components: ReportComponent[];
  Custom: boolean;
  Id?: number;
  SectionId: number;
  TabGroup?: string;
  Title: string;
}

interface LegendProps {
  Orientation: 'vertical' | 'horizontal' | undefined;
  Position: 'top' | 'bottom' | 'left' | 'right' | 'custom' | undefined;
}

export interface ReportComponent {
  CenterTitle?: string;
  ColorSections?: ColorSection[];
  ComponentId: number;
  ComponentName: string;
  ConvertToFlatList: boolean;
  CustomProperties: any;
  DataSource: string;
  Description?: string;
  HeaderStyles: string;
  Legend?: LegendProps;
  ReportComponentId: number;
  ReportId: number;
  Results?: ResultData;
  ResultsCount: number;
  Section: number;
  Title?: string;
  Type: string | null;
  Width: number;
  WidthLg: number;
  WidthMd: number;
  WidthSm: number;
  WidthXl: number;
}

export interface ResultData {
  Categories?: string[];
  Columns?: any[];
  Data: any; // ResultItem[][] | SkillTreeItem[];
  DataType: string;
  NotNullColumns?: string[];
  SeriesList: SeriesList[];
  Template?: any;
  Title: string;
  XAxisType: 'date' | 'numeric' | 'log' | undefined;
  XTitle?: string;
  YAxisType: 'date' | 'numeric' | 'log' | undefined;
  YTitle?: string;
}

export interface SeriesList {
  Color?: string;
  Data: ResultItem[];
  ExtendedProperties?: ExtendedProperty;
  Name: string;
  Type: string;
}

export interface ExtendedProperty {
  xField?: string;
  yField?: string;
}

export interface ResultItem {
  Action?: string;
  Answered: boolean;
  Color: string;
  Date: string;
  Description: string;
  Details?: { Columns: any[]; Data: any[] };
  DisplayOrder?: number;
  HoverText?: string;
  QuestionName: string;
  Series?: string;
  Share: string;
  Title: string;
}

export interface SkillTreeItem {
  Color: string;
  Data?: ResultItem[];
  Id: number;
  items?: SkillTreeItem[];
  text: string; //Name
  Type: string;
  Value: string;

  //Objectives?: SkillTreeItem[];
  //Subobjectives?: SkillTreeItem[];
}

export interface ReportFilter {
  ChildFilters: ReportFilter[];
  DataSourceProcedureName: string;
  DataType: string;
  DefaultValue: string;
  DisplayName: string;
  DisplayOrder: number;
  FieldType: string;
  FilterOptions?: Option[];
  FilterRequired: boolean;
  Id: number;
  MultiSelect: boolean;
  Name: string;
  ParentFilterId: number;
  ParentValue: string;
  ReportId: number;
  Type: string;
  Value: string;
  ViewMode: string;
}

export interface Option {
  ProductId: string;
  ProductName: string;
  Text: string;
  Value: string;
}

export interface FilterValue {
  Id: number;
  Value: string;
}

export interface RunReportRequest {
  AllowMissingFilters?: boolean;
  CultureId: number;
  Filters: FilterValue[];
  ReportId: number;
  ScopeObjectId: number;
}

export enum ReportFilterType {
  StartEndDate = 1,
  TransactionsCSV = 29,
  StudentGroupsCSV = 31,
  CultureId = 32,
}

export enum TestType {
  Application = 1,
  Simulation = 2,
  Linear = 3,
  Project = 4,
  Skorm = 5,
  ProjectV2 = 6,
  Exercise = 7,
  multiproject = 8,
  LinearApplication = 9,
  CustomProject = 10,
  AdobeLITA = 11,
  APIIntegration = 12,
  MultiSection = 13,
  LTIAdvantageToolLaunch = 14,
  AdobeAcrobat = 15,
}

/**
 * Represents an api handler for the React Reports page and hits the Areas\Api\Controllers\ReportController.cs file.
 * */
export class ReportApi {

    private static apiCaller = new ApiCaller('Reports');

    public static GetAll(cultureId = 1): Promise<CategoryReport[]> {

        return this.apiCaller.GET<CategoryReport[]>('GetReports', { cultureId });

    }

    public static GetReportLayout(
        reportId: number,
        scopeObjectId: number,
        cultureId = 1,
        autoRunReport = true
    ): Promise<Report> {

        return this.apiCaller.GET<Report>('GetReportLayout', {
            autoRunReport,
            cultureId,
            reportId,
            scopeObjectId
        });

    }

    public static RunReport(req: RunReportRequest): Promise<Report> {

        return this.apiCaller.POST<Report>('RunReport', req);

    }

    public static GetFilterOptions(
        filter: ReportFilter,
        parentValues: string,
        scopeId: number,
        scopeObjectId: number
    ): Promise<Record<string, Option[]>> {

        return this.apiCaller.POST<Record<string, Option[]>>('GetFilterOptions', {
            filter,
            parentValues,
            scopeId,
            scopeObjectId
        });

    }

    public static DeleteCustomReport(
        customReportId: number
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.DELETE<FullfilledApiResponse>('DeleteCustomReport', {
            customReportId
        });

    }

    public static CreateUpdateCustomReport(
        customReportId: number,
        urlString: string,
        baseReportId: number,
        name: string,
        description: string,
        cultureId = 1
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>(
            'CreateUpdateCustomReport',
            {
                name,
                baseReportId,
                cultureId,
                customReportId,
                description,
                urlString
            }
        );

    }

    public static GetCustomReports(): Promise<Report[]> {

        return this.apiCaller.GET<Report[]>('GetCustomReports', {});

    }

    public static GetKaplanResults(studentProfileId: number, resultId: number): Promise<any> {

        return this.apiCaller.POST<any>('GetKaplanResults', { resultId, studentProfileId });

    }

}
